/**
 * F-308 — UK / PRS feature gate for the Texas market.
 *
 * Texas ships its own route registry (nav/routeRegistry.ts) and its own
 * landing/dashboard. Everything below is a UK-legal or PRS-specific feature
 * that must NOT be reachable when REACT_APP_MARKET=texas: route paths are
 * listed here so App.tsx can redirect them to /dashboard and so tests can
 * assert the list without rendering the router.
 *
 * The UK lane is untouched — the gate only ever redirects when the market
 * is texas. Components themselves are never deleted, only unlinked.
 */

export const UK_ONLY_ROUTES: string[] = [
    '/features',                 // UK PRS marketing feature page
    '/tenants',                  // Right to Rent / assured shorthold wizard
    '/certificates',             // EPC / Gas Safety / EICR certificate suite
    '/certificate-orders',       // UK certificate ordering + payment
    '/evictions',                // UK Section 8 / 21 eviction workflow
    '/arrears',                  // UK rent arrears (ground 8)
    '/agreements',               // UK tenancy agreements
    '/aml',                      // UK HMRC MLR checks (excluded from US_MODULES)
    '/awaab-law',                // Awaab's Law (UK social housing)
    '/gdpr',                     // UK/EU GDPR dashboard
    '/repairs-tracking',         // UK housing repairs queue
    '/case-linking',             // UK repairs case linking
    '/nlp-analyzer',             // UK tenancy-agreement NLP (£ / Section 21 samples)
    '/dashboard/finance',        // UK MTD VAT Return + £ ledger
    '/food-safety-pro',          // UK food business module
    '/food-safety',              // UK food business module
    '/local-gov',                // UK council portal
    '/partners',                 // UK trade-partner directory (Gas Safe / EPC Assessor)
    '/affiliates',               // affiliate payouts denominated in £
];

/** Strip router syntax (`/x/:id?`, `/x/*`) down to its bare path segment. */
const normalizeRoutePath = (path: string): string => {
    const cut = path.search(/[:*]/);
    let base = cut === -1 ? path : path.slice(0, cut);
    if (base.length > 1 && base.endsWith('/')) base = base.slice(0, -1);
    return base || '/';
};

/** True when `path` is a UK-only route that Texas must not serve. */
export const isUkOnlyPath = (path: string): boolean => {
    const base = normalizeRoutePath(path);
    return UK_ONLY_ROUTES.some((route) => base === route || base.startsWith(`${route}/`));
};
